import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { customToast } from "utils/customToast";

const WishlistContext = createContext({
  wishlistData: {},
  toggleWishlist: () => {},
  clearWishlist: () => {},
  isWishListed: () => false,
});

const DB_NAME = "FavoritesDB";
const DB_VERSION = 1;
const STORE_NAME = "favorites";

export const DB_OPS = {
  ADD: "add",
  REMOVE: "remove",
  CLEAR: "clear",
};

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const WishlistProvider = ({ children }) => {
  const [wishlistData, setWishlistData] = useState(new Map());

  const updateIndexedDB = useCallback(async (data, type) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        if (type === DB_OPS.ADD) {
          store.put({
            id: data.id,
            type: data.roomTypeSlug ? "roomTypes" : "properties",
            data: {
              id: data.id,
              propertySlug: data.propertySlug,
              roomTypeSlug: data.roomTypeSlug || null,
            },
          });
        } else if (type === DB_OPS.REMOVE) {
          store.delete(data.id);
        } else if (type === DB_OPS.CLEAR) {
          store.clear();
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () =>
          reject(tx.error || new Error("Error updating indexedDB"));
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  const isWishListed = useCallback(
    (id) => {
      return wishlistData?.has?.(id);
    },
    [wishlistData]
  );

  const toggleWishlist = useCallback(
    (data) => {
      const isExisting = isWishListed(data.id);

      updateIndexedDB(data, isExisting ? DB_OPS.REMOVE : DB_OPS.ADD)
        .then(() => {
          setWishlistData((prev) => {
            const next = new Map(prev);
            if (isExisting) {
              next.delete(data.id);
            } else {
              next.set(data.id, data);
            }
            return next;
          });
          if (data.id.includes("room-")) {
            customToast(
              "Success!",
              isExisting
                ? "Room type removed from favorites"
                : "Room type added to favorites",
              "success"
            );
          } else {
            customToast(
              "Success!",
              isExisting
                ? "Property removed from favorites"
                : "Property added to favorites",
              "success"
            );
          }
        })
        .catch((err) => {
          console.error("Error toggling wishlist:", err);
          customToast("Error!", "Error toggling wishlist", "error");
        });
    },
    [isWishListed, updateIndexedDB]
  );

  const clearWishlist = useCallback(() => {
    updateIndexedDB({}, DB_OPS.CLEAR)
      .then(() => {
        setWishlistData(new Map());
      })
      .catch((error) => {
        console.error("Error clearing wishlist:", error);
        customToast("Error!", "Error clearing wishlist", "error");
      });
  }, [updateIndexedDB]);

  useEffect(() => {
    const initWishlist = async () => {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = (event) => {
        const result = Array.isArray(event?.target?.result)
          ? event.target.result
          : [];
        const entries = result
          .map((item) => [item?.id, item?.data])
          .filter(Boolean);
        setWishlistData(new Map(entries));
      };
      request.onerror = () => {
        setWishlistData(new Map());
      };
    };
    initWishlist();
  }, []);

  return (
    <WishlistContext.Provider
      value={{
        wishlistData,
        toggleWishlist,
        clearWishlist,
        isWishListed,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
